import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Check, CircleHelp, Lightbulb, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBrandAuth } from "@/hooks/useBrandAuth";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PlanFeature } from "@/types/permissions";
import {
  brandService,
  OnboardingStatusResponse,
} from "@/services/brand.service";
import {
  buildOnboardingSteps,
  BrandOnboardingStep,
  isOnboardingComplete,
  skipOnboardingToDashboard,
} from "@/lib/brandOnboarding";
import { cn } from "@/lib/utils";
import { OnboardingSkeleton } from "./OnboardingSkeleton";
import { OnboardingVideoPlayer } from "./OnboardingVideoPlayer";

export default function OnboardingDashboard() {
  const navigate = useNavigate();
  const { user } = useBrandAuth();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const [onboardingData, setOnboardingData] =
    useState<OnboardingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);

  const hasPostCheckout = hasFeature(PlanFeature.POST_CHECKOUT_REFERRALS);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await brandService.getOnboardingStatus();
        setOnboardingData(data);
      } catch (error) {
        console.error("Failed to fetch onboarding status:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const activeSteps = useMemo(
    () => buildOnboardingSteps(onboardingData, hasPostCheckout),
    [onboardingData, hasPostCheckout]
  );
  const completedCount = activeSteps.filter((s) => s.completed).length;
  const firstName = user?.name?.split(" ")[0] || "there";

  useEffect(() => {
    if (activeSteps.length === 0) return;
    const preferred =
      activeSteps.find((s) => !s.completed)?.id ?? activeSteps[0].id;
    setSelectedStepId((current) => {
      if (current != null && activeSteps.some((s) => s.id === current)) {
        return current;
      }
      return preferred;
    });
  }, [activeSteps]);

  const selectedStep: BrandOnboardingStep | undefined = activeSteps.find(
    (s) => s.id === selectedStepId
  );

  const handleGoToDashboard = () => {
    skipOnboardingToDashboard();
    navigate("/brand/dashboard");
  };

  const handleStartStep = (step: BrandOnboardingStep) => {
    if (step.completed) return;
    navigate(step.path);
  };

  if (loading || permissionsLoading) {
    return <OnboardingSkeleton />;
  }

  if (isOnboardingComplete(activeSteps)) {
    return <Navigate to="/brand/dashboard" replace />;
  }

  return (
    <div className="flex-1 pb-16 text-foreground">
      <div className="mb-6 flex items-center justify-end gap-4 text-sm text-muted-foreground">
        <Link
          to="/brand/support"
          className="hidden items-center gap-2 transition-colors hover:text-foreground sm:flex"
        >
          <CircleHelp aria-hidden="true" className="size-4" />
          Help center
        </Link>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGoToDashboard}
        >
          Go to dashboard
        </Button>
      </div>

      <div className="mx-auto max-w-3xl">
        <section className="text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-brand-600/10 px-3 py-1.5 text-xs font-medium text-brand-600">
            <Lightbulb aria-hidden="true" className="size-3.5" />
            Getting started
          </div>
          <h1 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
            Let&apos;s get you set up, {firstName}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-pretty text-sm leading-6 text-muted-foreground sm:text-base">
            Finish a few steps to get the most out of Reveshare. You can explore
            the dashboard anytime and come back here later.
          </p>
        </section>

        <div
          className="mt-10 flex items-center gap-3"
          aria-label={`${completedCount} of ${activeSteps.length} tasks complete`}
        >
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand-600 transition-all duration-500"
              style={{
                width: `${
                  activeSteps.length
                    ? (completedCount / activeSteps.length) * 100
                    : 0
                }%`,
              }}
            />
          </div>
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {completedCount} of {activeSteps.length} done
          </span>
        </div>

        <section className="mt-5 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="relative aspect-video overflow-hidden bg-muted/60">
            {selectedStep?.videoUrl ? (
              <OnboardingVideoPlayer
                key={selectedStep.id}
                className="absolute inset-0"
                src={selectedStep.videoUrl}
                title={selectedStep.title}
              />
            ) : (
              <>
                <div className="absolute inset-0 animate-pulse bg-muted" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex size-16 items-center justify-center rounded-full bg-brand-600 text-brand-contrast shadow-lg sm:size-20">
                    <Play
                      aria-hidden="true"
                      className="ml-1 size-6 fill-current sm:size-7"
                    />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 rounded-md bg-background/90 px-3 py-2 text-xs font-medium text-foreground backdrop-blur">
                  Video coming soon
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-border px-5 py-4">
            <div>
              <p className="text-sm font-medium">
                {selectedStep?.title ?? "Select a step"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {selectedStep?.description ??
                  "Tap a task below to learn more"}
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Your next steps
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                A few small steps to get up and running.
              </p>
            </div>
            <span className="hidden text-xs text-muted-foreground sm:block">
              Tap a task to learn more
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {activeSteps.map((step) => {
              const isSelected = selectedStepId === step.id;
              const isCompleted = step.completed;
              return (
                <div
                  key={step.id}
                  className={cn(
                    "flex w-full items-center gap-4 rounded-xl border p-4 transition-all",
                    isSelected
                      ? "border-brand-600/30 bg-brand-600/5 shadow-sm"
                      : "border-border bg-card hover:bg-muted/50"
                  )}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-4 text-left"
                    onClick={() => setSelectedStepId(step.id)}
                  >
                    <span
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full border",
                        isCompleted
                          ? "border-brand-600 bg-brand-600 text-brand-contrast"
                          : isSelected
                            ? "border-brand-600 text-brand-600"
                            : "border-border text-transparent"
                      )}
                    >
                      <Check aria-hidden="true" className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block text-sm font-semibold",
                          isCompleted && "text-muted-foreground line-through"
                        )}
                      >
                        {step.title}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {step.description}
                      </span>
                    </span>
                  </button>
                  {isCompleted ? (
                    <span className="hidden rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground sm:inline-flex">
                      Complete
                    </span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      className="hidden shrink-0 sm:inline-flex"
                      onClick={() => {
                        if (!isSelected) {
                          setSelectedStepId(step.id);
                          return;
                        }
                        handleStartStep(step);
                      }}
                    >
                      {isSelected ? "Start" : "View"}
                    </Button>
                  )}
                  <ArrowRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-6 flex flex-col gap-4 rounded-2xl bg-muted/60 p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="text-sm font-semibold">Ready to dive in?</h2>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              You can always come back to these steps later.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {selectedStep && !selectedStep.completed && (
              <Button
                type="button"
                onClick={() => handleStartStep(selectedStep)}
              >
                Take me there
                <ArrowRight className="ml-2 size-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoToDashboard}
            >
              Go to dashboard
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
